import { openDB, type IDBPDatabase, type DBSchema } from "idb";
import type { Card, Pack, PackProgress, Player, Session } from "../domain/types";

// Renamed each time we add a store so Chrome doesn't get stuck on an upgrade
// when the prior tab still holds a connection. Cheap to bump for now since
// we have no production users yet.
export const DB_NAME = "portal-party-v3";
export const DB_VERSION = 1;

/**
 * Persisted roster of players the host saved on a previous session so they
 * can pick "the same crew" again without retyping. Editable — new players
 * appended, removed ones disappear.
 */
export interface PlayerGroup {
  id: string;
  name: string;
  players: Player[];
  createdAt: number;
  updatedAt: number;
  /** Used for sort-by-recency in the Players screen list. */
  lastUsedAt: number;
}

export interface PPSchema extends DBSchema {
  cards: {
    key: string;
    value: Card;
    indexes: {
      by_pack: string;
      by_pack_lang: [string, string];
      by_mode: string;
    };
  };
  packs: {
    key: string;
    value: Pack;
    indexes: {
      by_mode: string;
    };
  };
  pack_progress: {
    key: string; // packId
    value: PackProgress;
  };
  sessions: {
    key: string;
    value: Session;
    indexes: {
      by_endedAt: number;
      by_startedAt: number;
    };
  };
  meta: {
    key: string;
    value: { key: string; value: unknown };
  };
  player_groups: {
    key: string;
    value: PlayerGroup;
    indexes: {
      by_lastUsedAt: number;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<PPSchema>> | null = null;

export function getDB(): Promise<IDBPDatabase<PPSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<PPSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("cards")) {
          const cards = db.createObjectStore("cards", { keyPath: "id" });
          cards.createIndex("by_pack", "packId");
          cards.createIndex("by_pack_lang", ["packId", "language"]);
          cards.createIndex("by_mode", "modeId");
        }
        if (!db.objectStoreNames.contains("packs")) {
          const packs = db.createObjectStore("packs", { keyPath: "id" });
          packs.createIndex("by_mode", "modeId");
        }
        if (!db.objectStoreNames.contains("pack_progress")) {
          db.createObjectStore("pack_progress", { keyPath: "packId" });
        }
        if (!db.objectStoreNames.contains("sessions")) {
          const sessions = db.createObjectStore("sessions", { keyPath: "id" });
          sessions.createIndex("by_endedAt", "endedAt");
          sessions.createIndex("by_startedAt", "startedAt");
        }
        if (!db.objectStoreNames.contains("meta")) {
          db.createObjectStore("meta", { keyPath: "key" });
        }
        if (!db.objectStoreNames.contains("player_groups")) {
          const pg = db.createObjectStore("player_groups", { keyPath: "id" });
          pg.createIndex("by_lastUsedAt", "lastUsedAt");
        }
      },
    });
  }
  return dbPromise;
}

/** Reset the connection — used by tests / settings reset. */
export function resetConnection(): void {
  dbPromise = null;
}
